import React, { useEffect, useState } from 'react';

import {
  ActionIcon,
  Alert,
  Button,
  Center,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Stack,
  Text,
  createStyles,
  useMantineTheme,
} from '@mantine/core';
import { IconExclamationCircle, IconPlus } from '@tabler/icons-react';
import AthleteEventStatusBadge from 'components/status-badges/AthleteEventStatusBadge';
import WorkoutStatusIcon from 'components/Workout/WorkoutStatusIcon';
import { useAuth } from 'context/auth-context';
import dayjs from 'dayjs';
import useAthleteEvents from 'hooks/athleteEvent/useAthleteEvents';
import useTotalAttended from 'hooks/athleteEvent/useTotalAttended';
import SimpleHeader from 'layouts/MobileLayout/components/SimpleHeader';
import { useLocation, useNavigate } from 'react-router-dom';
import { AthleteEvent } from 'types';
import { isWorkoutTrackerEnabled } from 'utils/workoutTracker';

const useStyles = createStyles({
  badgeContainer: {
    position: 'absolute',
    top: -10,
    right: 10,
  },
});

const today = new Date();
today.setHours(0);
today.setMinutes(0);
today.setSeconds(0);

const Events = (): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { session } = useAuth();
  const workoutTrackerEnabled = isWorkoutTrackerEnabled(session?.gym?.workoutTrackerEnabled);

  const [upcomingOrPast, setUpcomingOrPast] = useState<string>(state?.upcomingOrPast || 'upcoming');
  const [groupedAthleteEvents, setGroupedAthleteEvents] = useState<{ [startDate: string]: AthleteEvent[] }>(
    {},
  );

  const isPast = upcomingOrPast === 'past';
  const isUpcoming = !isPast;

  const { data, isSuccess, isError, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useAthleteEvents({
      athlete: session?.athlete?.athleteId,
      startDateRange: isUpcoming ? dayjs(today).format('YYYY-MM-DD') : undefined,
      endDateRange: isPast ? dayjs(today).format('YYYY-MM-DD') : undefined,
      type: upcomingOrPast as any,
      ordering: isUpcoming ? 'start_date' : '-start_date',
      limit: '20',
    });

  const { data: totalAttendedData } = useTotalAttended({
    athlete: session?.athlete?.athleteId,
    type: 'past',
    enabled: isPast && !!session?.athlete?.athleteId,
  });

  const handlePlusClick = (): void => {
    navigate('/schedule');
  };

  const handleEventCardClick = (eventId: string, type: string): void => {
    if (type === 'athlete_kilo_class') {
      navigate(`/classes/${eventId}`, {
        state: {
          prevPath: '/events',
          upcomingOrPast: upcomingOrPast,
        },
      });
    } else if (type === 'athlete_appointment') {
      navigate(`/appointments/${eventId}`, {
        state: {
          prevPath: '/events',
          upcomingOrPast: upcomingOrPast,
        },
      });
    }
  };

  const handleSeeAttendanceClick = (): void => {
    navigate(`/events/attendance`, {
      state: {
        prevPath: '/events',
        upcomingOrPast: upcomingOrPast,
      },
    });
  };

  useEffect(() => {
    if (isSuccess && data && session?.athlete) {
      const flatData = data.pages.map(page => page.data.results).flat();
      setGroupedAthleteEvents(
        flatData.reduce((groupedData: { [startDate: string]: AthleteEvent[] }, item) => {
          const itemStartDate = item.startDate;
          groupedData[itemStartDate] = groupedData[itemStartDate] || [];
          groupedData[itemStartDate].push(item);
          return groupedData;
        }, {}),
      );
    }
  }, [data, isSuccess, session?.athlete]);

  return (
    <>
      <SimpleHeader title='My Events' />

      <Stack className='content'>
        <Group position='apart' noWrap p='xs'>
          <SegmentedControl
            size='sm'
            value={upcomingOrPast}
            onChange={setUpcomingOrPast}
            data={[
              { label: 'Upcoming', value: 'upcoming' },
              { label: 'Past', value: 'past' },
            ]}
          />
          {!isLoading && isPast && (
            <Group spacing='xs' noWrap>
              {totalAttendedData?.data?.attendedTotalAmount != null && (
                <Text size='sm' color='dimmed' sx={{ whiteSpace: 'nowrap' }}>
                  Attended{' '}
                  <Text span fw={700} color='dark'>
                    {totalAttendedData.data.attendedTotalAmount}
                  </Text>
                </Text>
              )}
              <Button
                compact
                color='kiloBlue'
                onClick={handleSeeAttendanceClick}
                disabled={Object.keys(groupedAthleteEvents).length === 0}
              >
                See Attendance
              </Button>
            </Group>
          )}
        </Group>

        {isError && (
          <Center sx={{ flexGrow: 1 }} p='lg'>
            <Alert icon={<IconExclamationCircle size={theme.fontSizes.md} />} title='Error' color='red'>
              Something went wrong while fetching data!
            </Alert>
          </Center>
        )}

        {!isLoading && Object.keys(groupedAthleteEvents).length === 0 && (
          <Center>
            <Text color='dimmed'>
              {upcomingOrPast === 'upcoming'
                ? `You don't have any upcoming events.`
                : `You don't have any past events.`}
            </Text>
          </Center>
        )}

        {Object.keys(groupedAthleteEvents).length > 0 && (
          <Stack spacing='md'>
            {Object.keys(groupedAthleteEvents).map(eventStartDate => (
              <Stack key={eventStartDate} spacing='sm'>
                <Divider mx='-md' />
                <Text size='lg' color='dark.3' weight='bold'>
                  {dayjs(eventStartDate).format('dddd, MMMM D')}
                </Text>

                <Stack spacing='lg'>
                  {groupedAthleteEvents[eventStartDate].map(athleteEvent => {
                    const fullStartDate = dayjs(`${athleteEvent.startDate}T${athleteEvent.startTime}`);
                    return (
                      <Paper
                        key={athleteEvent.athleteEventId}
                        shadow='xs'
                        p='sm'
                        radius='lg'
                        pos='relative'
                        onClick={() => handleEventCardClick(athleteEvent.eventId, athleteEvent.type)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <AthleteEventStatusBadge
                          isReserved={athleteEvent?.isReserved}
                          isCheckedIn={athleteEvent?.isCheckedIn}
                          isWaitlisted={athleteEvent?.isWaitlisted}
                          className={classes.badgeContainer}
                          waitlistOrder={athleteEvent.waitlistOrder}
                          isLateCancel={athleteEvent?.isLateCancel}
                          isNoShow={athleteEvent?.isNoShow}
                          isCredited={athleteEvent?.isCredited}
                        />

                        <Group position='apart' noWrap px='xs'>
                          <Text size='md' color='kiloBlue' weight='bold' lineClamp={1}>
                            {athleteEvent.name}
                          </Text>
                          <Group spacing='xs' noWrap>
                            {isPast &&
                              athleteEvent.type === 'athlete_kilo_class' &&
                              workoutTrackerEnabled && <WorkoutStatusIcon hasLoggedResults={false} />}
                            <Text size='sm' color='dimmed' weight='bold' sx={{ textWrap: 'nowrap' }}>
                              {`${fullStartDate.format('h:mma')} - ${fullStartDate
                                .add(athleteEvent.duration, 'minutes')
                                .format('h:mma')}`}
                            </Text>
                          </Group>
                        </Group>
                      </Paper>
                    );
                  })}
                </Stack>
              </Stack>
            ))}
          </Stack>
        )}

        {hasNextPage && (
          <Group grow>
            <Button size='md' variant='light' onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
              Show more
            </Button>
          </Group>
        )}

        <ActionIcon
          color='kiloBlue'
          variant='filled'
          size={62}
          radius='xl'
          sx={{
            position: 'sticky',
            marginLeft: 'auto',
            marginTop: 'auto',
            bottom: 0,
            boxShadow: theme.shadows.xl,
          }}
          onClick={handlePlusClick}
        >
          <IconPlus size={theme.spacing.xl} />
        </ActionIcon>
      </Stack>
    </>
  );
};

export default Events;
